import React from 'react';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { GoogleOAuthProvider } from '@react-oauth/google';
import './scss/style.scss';

const loading = (
    <div className="pt-3 text-center">
        <div className="sk-spinner sk-spinner-pulse"></div>
    </div>
);

// Containers
const TheLayout = React.lazy(() => import('./containers/TheLayout'));
const PortalRouter = React.lazy(() => import('./views/portal/PortalRouter'));

// Pages
const Login = React.lazy(() => import('./views/pages/login/Login'));
const Register = React.lazy(() => import('./views/pages/register'));
const ForgotPassword = React.lazy(() => import('./views/pages/login/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./views/pages/login/ResetPassword'));
const TermsOfService = React.lazy(() => import('./views/pages/legal/TermsOfService'));
const PrivacyPolicy = React.lazy(() => import('./views/pages/legal/PrivacyPolicy'));
const Page404 = React.lazy(() => import('./views/pages/page404/Page404'));
const Page500 = React.lazy(() => import('./views/pages/page500/Page500'));

const queryClient = new QueryClient();

function App() {
    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

    const routerTree = (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <React.Suspense fallback={loading}>
                    <Switch>
                        <Route exact path="/login" name="Login Page" render={(props) => <Login {...props} />} />
                        <Route exact path="/register" name="Register Page" render={(props) => <Register {...props} />} />
                        <Route exact path="/forgot" name="Forgot Password Page" render={(props) => <ForgotPassword {...props} />} />
                        <Route exact path="/reset-password/:token" name="Reset Password Page" render={(props) => <ResetPassword {...props} />} />
                        <Route exact path="/terms" name="Terms of Service" render={(props) => <TermsOfService {...props} />} />
                        <Route exact path="/privacy" name="Privacy Policy" render={(props) => <PrivacyPolicy {...props} />} />
                        <Route exact path="/404" name="Page 404" render={(props) => <Page404 {...props} />} />
                        <Route exact path="/500" name="Page 500" render={(props) => <Page500 {...props} />} />
                        <Route path="/portal" name="Members Portal" render={(props) => <PortalRouter {...props} />} />
                        <Route path="/" name="Home" render={(props) => <TheLayout {...props} />} />
                    </Switch>
                </React.Suspense>
            </BrowserRouter>
        </QueryClientProvider>
    );

    // Mount the Google OAuth provider only when a real clientId is configured — an empty clientId
    // makes the Google script throw "Missing required parameter client_id". Pages render their Google
    // buttons (and call useGoogleLogin) only when a clientId exists, so on localhost without
    // REACT_APP_GOOGLE_CLIENT_ID the provider is simply absent and nothing calls the hook.
    if (!googleClientId) {
        return routerTree;
    }

    return (
        <GoogleOAuthProvider clientId={googleClientId}>
            {routerTree}
        </GoogleOAuthProvider>
    );
}

export default App;
